/**
 * A radio group of buttons: `pills` (a variable's Type, a key's field) or `cards` (where a secret comes from). Shared by
 * the app's Variables and secrets and the key picker (KeyFieldPicker). Keyboard as a native radio group: one tab stop
 * (the checked option), arrow keys / Home / End move and select, skipping a disabled option.
 */
import type { KeyboardEvent, ReactNode } from 'react'

export type ChoiceOption<K extends string> = { key: K; label: ReactNode; hint?: ReactNode; icon?: ReactNode; disabled?: boolean; testId: string }

export function Choice<K extends string>({ label, value, options, onChange, variant = 'pills', testId }: {
  label: string
  value: K
  options: Array<ChoiceOption<K>>
  onChange: (k: K) => void
  variant?: 'pills' | 'cards'
  testId?: string
}) {
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const enabled = options.filter(o => !o.disabled)
    const i = enabled.findIndex(o => o.key === value)
    const n = enabled.length
    const next = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? (i + 1) % n
      : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? (i - 1 + n) % n
      : e.key === 'Home' ? 0
      : e.key === 'End' ? n - 1
      : null
    if (next === null || n === 0) return
    e.preventDefault()
    const k = enabled[next].key
    if (k !== value) onChange(k)
    // Found by its data, never by a selector built from it: a key's field name can hold any character.
    ;[...e.currentTarget.querySelectorAll<HTMLButtonElement>('[data-key]')].find(b => b.dataset.key === k)?.focus()
  }
  // The tab stop is the checked option — unless it is disabled (an existing secret while the keybook is off), then the
  // first option that can take focus, so the group never drops out of the tab order.
  const checkedEnabled = options.some(o => o.key === value && !o.disabled)
  const tabStop = checkedEnabled ? value : options.find(o => !o.disabled)?.key
  const cards = variant === 'cards'
  return (
    <div
      role="radiogroup"
      aria-label={label}
      onKeyDown={onKeyDown}
      // Cards sit two across from a 28rem list (`@container/envlist`, the Variables and secrets table); stacked below.
      className={cards ? 'grid gap-2 @md/envlist:grid-cols-2' : 'inline-flex flex-wrap gap-0.5 p-0.5 rounded-md ws-glass-well'}
      data-testid={testId}
    >
      {options.map(o => {
        const on = value === o.key
        return (
          <button
            key={o.key}
            type="button"
            role="radio"
            data-key={o.key}
            aria-checked={on}
            tabIndex={tabStop === o.key ? 0 : -1}
            onClick={() => onChange(o.key)}
            disabled={o.disabled}
            className={cards
              ? `text-left flex items-start gap-2.5 px-3 py-2.5 rounded-md border transition-colors disabled:opacity-40 ${on ? 'border-blue-400/70 bg-blue-500/10' : 'themed-border themed-bg-hover'}`
              : `text-xs px-3 py-1 rounded inline-flex items-center gap-1.5 transition-colors ${on ? 'themed-btn-primary' : 'themed-text-muted hover:themed-text'} disabled:opacity-40`}
            data-testid={o.testId}
          >
            {cards ? (
              <>
                <span className={`shrink-0 mt-0.5 ${on ? 'text-blue-400' : 'themed-icon'}`}>{o.icon}</span>
                <span className="min-w-0">
                  <span className="block text-sm themed-text">{o.label}</span>
                  {o.hint && <span className="block text-[11px] themed-text-muted mt-0.5">{o.hint}</span>}
                </span>
              </>
            ) : <>{o.icon}{o.label}</>}
          </button>
        )
      })}
    </div>
  )
}
